import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Smartphone, Globe } from 'lucide-react';
export function Hero() {
  return <section className="relative min-h-[80vh] flex items-center bg-slate-900 overflow-hidden pt-10 pb-20" data-id="element-172">
      {/* Background Gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-173">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-red-600/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3" data-id="element-174"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" data-id="element-175"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10 grid lg:grid-cols-2 gap-12 items-center" data-id="element-176">
        {/* Text Content */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-177">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-900/30 border border-red-500/30 text-red-400 text-sm font-medium mb-6" data-id="element-178">
            <span className="relative flex h-2 w-2" data-id="element-179">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-180"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" data-id="element-181"></span>
            </span>
            Available for new projects
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6" data-id="element-182">
            We Build{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500" data-id="element-183">
              Digital Excellence
            </span>{' '}
            For Your Business
          </h1>

          <p className="text-lg text-slate-400 mb-8 max-w-xl leading-relaxed" data-id="element-184">
            Hardy Blend transforms ideas into powerful digital solutions. From
            stunning websites to robust mobile applications, we engineer success
            for the modern web.
          </p>

          <div className="flex flex-col sm:flex-row gap-4" data-id="element-185">
            <a href="#contact-form" className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-all shadow-lg shadow-red-900/20 flex items-center justify-center gap-2 group" data-id="element-186">
              Start Your Project
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-187" />
            </a>
            <a href="#portfolio" className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-lg transition-all border border-slate-700 flex items-center justify-center" data-id="element-188">
              View Portfolio
            </a>
          </div>

          <div className="mt-12 flex items-center gap-8 text-slate-500" data-id="element-189">
            <div className="flex items-center gap-2" data-id="element-190">
              <Code className="w-5 h-5 text-red-500" data-id="element-191" />
              <span className="text-sm font-medium" data-id="element-192">Clean Code</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-193">
              <Smartphone className="w-5 h-5 text-red-500" data-id="element-194" />
              <span className="text-sm font-medium" data-id="element-195">Mobile First</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-196">
              <Globe className="w-5 h-5 text-red-500" data-id="element-197" />
              <span className="text-sm font-medium" data-id="element-198">SEO Optimized</span>
            </div>
          </div>
        </motion.div>

        {/* Visual Content */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative hidden lg:block" data-id="element-199">
          <div className="relative w-full aspect-square max-w-[600px] mx-auto" data-id="element-200">
            {/* Abstract Tech Illustration */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl border border-slate-700 shadow-2xl overflow-hidden" data-id="element-201">
              {/* Grid Pattern */}
              <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
              backgroundSize: '30px 30px'
            }} data-id="element-202"></div>

              {/* Floating Cards */}
              <motion.div animate={{
              y: [0, -20, 0]
            }} transition={{
              repeat: Infinity,
              duration: 6,
              ease: 'easeInOut'
            }} className="absolute top-12 left-12 right-12 h-64 bg-slate-800 rounded-xl border border-slate-600 shadow-xl p-6 flex flex-col gap-4" data-id="element-203">
                <div className="flex items-center gap-4 border-b border-slate-700 pb-4" data-id="element-204">
                  <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-205"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-206"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-207"></div>
                  <div className="flex-1 bg-slate-700 h-6 rounded-md opacity-50" data-id="element-208"></div>
                </div>
                <div className="flex-1 grid grid-cols-3 gap-4" data-id="element-209">
                  <div className="col-span-2 bg-slate-700/50 rounded-lg" data-id="element-210"></div>
                  <div className="bg-slate-700/50 rounded-lg" data-id="element-211"></div>
                  <div className="bg-slate-700/50 rounded-lg" data-id="element-212"></div>
                  <div className="col-span-2 bg-slate-700/50 rounded-lg" data-id="element-213"></div>
                </div>
              </motion.div>

              <motion.div animate={{
              y: [0, 20, 0]
            }} transition={{
              repeat: Infinity,
              duration: 7,
              ease: 'easeInOut',
              delay: 1
            }} className="absolute -bottom-10 -right-10 w-64 h-64 bg-red-600/90 backdrop-blur-md rounded-2xl shadow-2xl p-6 flex items-center justify-center transform rotate-12" data-id="element-214">
                <div className="text-center text-white" data-id="element-215">
                  <Smartphone className="w-16 h-16 mx-auto mb-4" data-id="element-216" />
                  <div className="font-bold text-xl" data-id="element-217">App Dev</div>
                  <div className="text-red-100 text-sm" data-id="element-218">iOS & Android</div>
                </div>
              </motion.div>

              <motion.div animate={{
              x: [0, 10, 0]
            }} transition={{
              repeat: Infinity,
              duration: 5,
              ease: 'easeInOut',
              delay: 0.5
            }} className="absolute bottom-20 -left-10 w-48 h-20 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 shadow-xl flex items-center gap-4 px-4" data-id="element-219">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center" data-id="element-220">
                  <Code className="w-5 h-5 text-white" data-id="element-221" />
                </div>
                <div data-id="element-222">
                  <div className="h-2 w-20 bg-white/50 rounded mb-2" data-id="element-223"></div>
                  <div className="h-2 w-12 bg-white/30 rounded" data-id="element-224"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}